import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute } from '@angular/router';
import { tap } from 'rxjs';
import { Quiz } from '../../data/quiz';
import { OnboardingFlowService } from '../../services/onboarding-flow.service';
import { AudioTrack, AudioTrackComponent } from '../../shared/audio-track/audio-track.component';
import { UserReviewComponent } from '../../shared/user-review/user-review.component';
import { QuizListOptionComponent } from '../quiz-list-option/quiz-list-option.component';
import { Rive } from '@rive-app/canvas';

@Component({
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatSelectModule, MatFormFieldModule, ReactiveFormsModule, MatListModule, QuizListOptionComponent, MatChipsModule, AudioTrackComponent, UserReviewComponent],
  selector: 'app-onboarding-quiz-step',
  templateUrl: './quiz-step.component.html',
  styleUrls: ['./quiz-step.component.scss']
})
export class QuizStepComponent implements OnInit, AfterViewInit {
  data: Quiz;
  form: FormGroup;

  currentIndex: number;
  stepsCount: number;

  @ViewChildren('audioTrackRef') audioTracks!: QueryList<AudioTrackComponent>;
  @ViewChild('animation') riveAnimation: ElementRef;

  private riveInstance: Rive | undefined;

  private isAnimationChecked = false;
  private isViewInitted = false;

  constructor(
    private route: ActivatedRoute,
    private flowService: OnboardingFlowService,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.stepsCount = this.flowService.getQuizLength();

    this.route.paramMap
      .pipe(
        tap(params => {
          this.currentIndex = parseInt(params.get('index') || '0', 10);
          this.data = this.flowService.getQuizData(this.currentIndex);

          this.checkAnimation();

          if (!this.data) {
            this.flowService.goToStart();
            return;
          }

          this.flowService.setBackgroundImage(this.data.fullScreenImageFilePath || null);

          this.form = this.fb.group({ selectedOptions: [''] });
        })
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    this.isViewInitted = true;

    if (!this.isAnimationChecked) {
      this.checkAnimation();
    }
  }

  buttonClicked(): void {
    const selectedOptions = this.form.value.selectedOptions;

    if (this.data.isRequired && (!selectedOptions || !selectedOptions.length)) {
      return;
    }

    this.finish(selectedOptions);
  }

  voteClicked(choice: 'yes' | 'no' | 'miss'): void {
    this.finish([choice]);
  }

  optionClicked(): void {
    if (this.data.isMultiple) {
      return;
    }

    this.finish(this.form.value.selectedOptions);
  }

  onPlay(currentTrack: AudioTrackComponent, track: AudioTrack): void {
    this.audioTracks.forEach(trackComponent => {
      if (trackComponent !== currentTrack) {
        trackComponent.pause();
      }
    });
    
    this.flowService.rememberLastPlayedTrack(track);
  }

  getBackgroundImageUrl(): string {
    if (this.data.imageFilePath) {
      return `url(${this.data.imageFilePath})`;
    }

    if (this.data.rive?.backgroundImagePath) {
      return `url(${this.data.rive.backgroundImagePath})`;
    }

    return '';
  }

  private checkAnimation(): void {
    if (!this.isViewInitted) {
      return;
    }

    this.riveInstance?.stop();
    this.riveInstance?.cleanup();

    if (this.data.rive) {
      const canvas = this.riveAnimation.nativeElement;

      this.riveInstance = new Rive({
        src: `assets/rive/${this.data.rive.fileName}.riv`,
        canvas,
        autoplay: true,
        animations: this.data.rive.animations,
        onLoad: () => this.riveInstance?.resizeToCanvas(), //resizeDrawingSurfaceToCanvas(),
      });
    }


    this.isAnimationChecked = true;
  }

  private finish(options: string[]): void {
    this.flowService.handleFinishedStep(this.currentIndex, options);
  }
}
